import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Badge,
  Box,
  Button,
  Menu,
  Skeleton,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useTheme,
  Switch,
  FormControlLabel,
} from '@mui/material';
import { Add, Tune, FilterAltOff } from '@mui/icons-material';

import { SearchInput } from '../SearchInput/SearchInput';
import { Column, PaginationProps, Table } from '../Table';
import { SortingOptions } from '../../types';
import { FilterSelectorMenu } from '../FilterSelectorMenu';
import { MyFiltersMenu } from '../MyFiltersMenu';
import { GetFilterByIdDto } from '@bandido/shared/dtos';
import { SetURLSearchParams } from 'react-router-dom';

interface ActionToggle {
  key: string;
  isActive: boolean;
  onToggle: () => void;
  label: string;
  tooltip?: string;
}

interface Props<T> {
  onSearchChange: (value: string) => void;
  onCreate?: () => void;
  columns: Column<T>[];
  isLoading: boolean;
  stickyHeader?: boolean;
  filtersCount?: number;
  actionToggles?: ActionToggle[];
  statisticsButton?: JSX.Element;
  data?: {
    key: string;
    items: T[];
    totalCount: number;
  };
  filters?: {
    form: JSX.Element;
    filterListKey: string;
    filtersList?: {
      publicFilters: GetFilterByIdDto[];
      privateFilters: GetFilterByIdDto[];
    };
    searchParams?: URLSearchParams;
    setSearchParams?: SetURLSearchParams;
    onSelectFilter?: (filterId: string) => void;
    onClearFilters?: () => void;
  };
  pagination?: PaginationProps;
  sorting?: {
    options: SortingOptions;
    handler: (options: SortingOptions) => void;
  };
  height?: number | string;
  // Checkbox selection props
  enableCheckboxSelection?: boolean;
  selectedItems?: (string | number)[];
  onSelectedItemsChange?: (selectedItems: (string | number)[]) => void;
  checkboxActions?: JSX.Element;
}

export const ListContentLayout = ({
  pagination,
  columns,
  stickyHeader,
  data,
  isLoading,
  onSearchChange,
  onCreate,
  sorting,
  filters,
  filtersCount,
  actionToggles,
  statisticsButton,
  height,
  enableCheckboxSelection = false,
  selectedItems = [],
  onSelectedItemsChange,
  checkboxActions,
}: Props<unknown>) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const [filtersAnchorEl, setFiltersAnchorEl] = useState<null | HTMLElement>(null);

  const handleClickTuneButton = (event: React.MouseEvent<HTMLElement>) => {
    setFiltersAnchorEl(event.currentTarget);
  };

  const handleCloseFilters = () => {
    setFiltersAnchorEl(null);
  };

  return (
    <>
      <Box display="flex" alignItems="center" gap={theme.spacing(1)} marginBottom={theme.spacing(3)}>
        <Typography
          variant="subtitle1"
          align="left"
          color={theme.palette.text.primary}
          sx={{
            flexGrow: 1,
          }}
        >
          {data?.totalCount} {t('common.results')}
        </Typography>

        {/* Action Toggles - Switches sutiles */}
        {actionToggles && actionToggles.map((toggle) => (
          <Tooltip key={toggle.key} title={toggle.tooltip || toggle.label} arrow>
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={toggle.isActive}
                  onChange={toggle.onToggle}
                  sx={{
                    '& .MuiSwitch-switchBase.Mui-checked': {
                      color: theme.palette.primary.main,
                    },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                      backgroundColor: theme.palette.primary.main,
                    },
                  }}
                />
              }
              label={
                <Typography 
                  variant="body2" 
                  sx={{ 
                    color: toggle.isActive ? theme.palette.primary.main : theme.palette.text.secondary,
                    fontWeight: toggle.isActive ? 500 : 400,
                  }}
                >
                  {toggle.label}
                </Typography>
              }
              labelPlacement="start"
              sx={{ margin: 0 }}
            />
          </Tooltip>
        ))}

        <SearchInput placeholder={t('common.search')} onSearch={onSearchChange} />
        
        {filters?.filtersList && filters.filtersList.privateFilters.length > 0 && (
          <MyFiltersMenu
            privateFilters={filters.filtersList.privateFilters}
            searchParams={filters.searchParams}
            setSearchParams={filters.setSearchParams ?? (() => {})}
          />
        )}
        
        {filters?.filtersList && (
          <FilterSelectorMenu
            filtersList={filters.filtersList}
            searchParams={filters.searchParams ?? new URLSearchParams()}
            setSearchParams={filters.setSearchParams ?? (() => {})}
          />
        )}
        
        <Box display="flex" alignItems="center" gap={theme.spacing(0)}>
          <ToggleButtonGroup
            exclusive
            sx={{
              height: theme.spacing(5),
              '& button': {
                padding: theme.spacing(1),
                width: theme.spacing(5),
              },
            }}
          >
            <ToggleButton value="add" onClick={handleClickTuneButton} aria-label="Filters">
              <Badge badgeContent={filtersCount && filtersCount > 0 ? filtersCount : undefined} variant="standard" color="error">
                <Tune />
              </Badge>
            </ToggleButton>
          </ToggleButtonGroup>
          
          <Tooltip title={t('filters.clearAll')} arrow>
            <Box
              onClick={filters?.onClearFilters && (filtersCount && filtersCount > 0) ? filters.onClearFilters : undefined}
              aria-label="Clear filters"
              sx={{ 
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: theme.spacing(4),
                width: theme.spacing(4),
                color: (filtersCount && filtersCount > 0) && filters?.onClearFilters 
                  ? theme.palette.error.main 
                  : theme.palette.action.disabled,
                cursor: (filtersCount && filtersCount > 0) && filters?.onClearFilters 
                  ? 'pointer' 
                  : 'default',
                borderRadius: theme.spacing(0.5),
                '&:hover': (filtersCount && filtersCount > 0) && filters?.onClearFilters ? {
                  color: theme.palette.error.dark,
                } : {},
                transition: 'all 0.2s ease-in-out',
              }}
            >
              <FilterAltOff fontSize="small" />
            </Box>
          </Tooltip>

          {statisticsButton}
        </Box>
        
        <Menu anchorEl={filtersAnchorEl} open={Boolean(filtersAnchorEl && filters?.form)} onClose={handleCloseFilters}>
          {filters?.form}
        </Menu>

        {onCreate && <Tooltip title={t('common.new')} arrow>
          <Button 
            variant="contained"
            onClick={() => onCreate()}
            aria-label="Create new"
            sx={{ 
              height: theme.spacing(5),
              minWidth: theme.spacing(5),
              padding: theme.spacing(1),
            }}
          >
            <Add />
          </Button>
        </Tooltip>}
      </Box>

      {data?.totalCount !== undefined && data.totalCount > 0 && (
        <Table
          height={height}
          columns={columns}
          data={data}
          pagination={pagination}
          stickyHeader={Boolean(stickyHeader)}
          sorting={sorting}
          enableCheckboxSelection={enableCheckboxSelection}
          selectedItems={selectedItems}
          onSelectedItemsChange={onSelectedItemsChange}
          checkboxActions={checkboxActions}
        />
      )}

      {isLoading && <Skeleton height={theme.spacing(10)} variant="rounded" animation="wave" />}
    </>
  );
};
